import { indentUnit } from "@codemirror/language";
import { Compartment, EditorState, type Extension, RangeSet } from "@codemirror/state";
import { lineNumberMarkers, lineNumbers } from "@codemirror/view";
import { WHILECAT_CODE_EDITOR_SYNTAX_HIGHLIGHTING, getTheme } from "@whilecat/core/editors/code-area-theme.js";
import { CodeEditorView } from "@whilecat/core/editors/code-editor-view.js";
import { getLanguageExtensions } from "@whilecat/core/editors/configurators/extensions-factory.js";
import { placeholders } from "@whilecat/core/editors/decorators/placeholder-decorator.js";
import { tooltipExtension } from "@whilecat/core/editors/decorators/tooltip-extension.js";
import { getKeymapExtension } from "@whilecat/core/editors/extensions/keymap-extension.js";
import type { AbstractCodeRange } from "@whilecat/core/editors/extensions/readonly-extension/ranges/abstract-range.js";
import readOnlyRangesExtension from "@whilecat/core/editors/extensions/readonly-extension/read-only-extension.js";
import { getCheckBoxGutter } from "@whilecat/core/editors/gutters/line-marker-gutter.js";
import { isAbsent, isPresent } from "@whilecat/core/utils/objects.js";
import { isMobile } from "@whilecat/core/utils/screen-utils.ts";
import { requireNotNull, sanityCheck } from "@whilecat/core/utils/validation.js";
import { EditorView } from "codemirror";
import { basicSetup } from "./basic-setup.ts";

export type ButtonCallBack = () => void;

export type CodeEditorSettings = {
    code: string;
    withCheckboxes: boolean;
    editable: boolean;
    linenumbers: boolean;
    language: string;
    disabled: boolean;
    oneLineEditor: boolean;
    resetButtonCallBack?: ButtonCallBack | undefined;
    tooltips?: Object | undefined;
    showAnswerButtonCallBack?: ButtonCallBack | undefined;
    showAnswerButtonVisible: boolean;
    readonlyRanges?: Array<AbstractCodeRange> | undefined;
    foldRanges: Array<AbstractCodeRange>;
    focus?: boolean;
    highlightCurrentLine?: boolean;
};

export function viewBashEditorSettings(code: string) {
    return {
        code: code,
        withCheckboxes: false,
        editable: false,
        language: "bash",
        linenumbers: true,
        disabled: false,
        oneLineEditor: false,
        resetButtonCallBack: undefined,
        tooltips: undefined,
        showAnswerButtonCallBack: undefined,
        showAnswerButtonVisible: false,
        readonlyRanges: undefined,
        foldRanges: [],
    };
}

export function viewJavaEditorSettings(
    code: string,
    readonlyRanges: Array<AbstractCodeRange> = [],
    foldRanges: Array<AbstractCodeRange> | undefined = [],
    tooltips: Object | undefined = undefined,
): CodeEditorSettings {
    return defaultJavaEditorSettings(
        code,
        false,
        false,
        false,
        undefined,
        undefined,
        readonlyRanges,
        foldRanges,
        tooltips,
    );
}

export function defaultJavaEditorSettings(
    code: string,
    editable: boolean,
    withCheckboxes: boolean = false,
    showAnswerButtonVisible: boolean = true,
    showAnswerButtonCallBack: ButtonCallBack | undefined = undefined,
    resetButtonCallBack: ButtonCallBack | undefined = undefined,
    readonlyRanges: Array<AbstractCodeRange> = [],
    foldRanges: Array<AbstractCodeRange> | undefined = [],
    tooltips: Object | undefined = undefined,
): CodeEditorSettings {
    return {
        code: code,
        withCheckboxes: withCheckboxes,
        editable: editable,
        language: "java",
        linenumbers: true,
        disabled: false,
        oneLineEditor: false,
        resetButtonCallBack: resetButtonCallBack,
        tooltips: tooltips,
        showAnswerButtonCallBack: showAnswerButtonCallBack,
        showAnswerButtonVisible: showAnswerButtonVisible,
        readonlyRanges: readonlyRanges,
        foldRanges: foldRanges ?? [],
    };
}

export class CodeEditorFabric {
    private readonly settings: CodeEditorSettings;
    private readonly parentNode: HTMLElement;

    constructor(settings: CodeEditorSettings, parentNode: HTMLElement) {
        this.settings = requireNotNull(settings);
        this.parentNode = requireNotNull(parentNode);

        if (isPresent(settings.resetButtonCallBack) && isPresent(settings.showAnswerButtonCallBack)) {
            sanityCheck(
                settings.resetButtonCallBack != settings.showAnswerButtonCallBack,
                "Must be different functions",
            );
        }
    }

    build(): CodeEditorView {
        const editableCompartment = new Compartment();

        const editor = new EditorView({
            doc: this.settings.code,
            extensions: this.getExtensions(editableCompartment),
            parent: this.parentNode,
        });

        const editorView = new CodeEditorView(editor, this.settings, editableCompartment);

        this.setupFoldingRanges(editorView);

        return editorView;
    }

    private getExtensions(editableCompartment: Compartment) {
        let extensions = [
            ...getLanguageExtensions(this.settings.language),
            basicSetup(this.settings.highlightCurrentLine ?? true),
            getTheme(this.settings.oneLineEditor),
            WHILECAT_CODE_EDITOR_SYNTAX_HIGHLIGHTING,
            EditorView.lineWrapping,
            indentUnit.of("    "),
            EditorState.readOnly.of(!this.settings.editable),
            editableCompartment.of(EditorView.editable.of(this.settings.editable)),
            placeholders,
        ];

        extensions = this.addTooltipsExtension(extensions);

        if (this.settings.withCheckboxes) {
            extensions = [getCheckBoxGutter()].concat(extensions);
        }

        /* FIXME: think of more generic way so in the future
           it would be easier to find where mobile customization is placed */
        if (!this.settings.linenumbers || isMobile()) {
            extensions.push(lineNumberMarkers.of(RangeSet.empty));
        } else {
            extensions.push(lineNumbers());
        }

        if (isPresent(this.settings.readonlyRanges) && this.settings.readonlyRanges!.length > 0) {
            extensions = [
                ...extensions,
                readOnlyRangesExtension((targetState: EditorState) => {
                    return this.getReadOnlyRanges(targetState);
                }),
            ];
        }

        // keymap should always come first to catch events before default ones
        return [getKeymapExtension(), ...extensions];
    }

    private getReadOnlyRanges(targetState: EditorState): Array<{
        from: number | undefined;
        to: number | undefined;
    }> {
        requireNotNull(this.settings.readonlyRanges);
        return this.settings.readonlyRanges!.map(range => range.getRange(targetState));
    }

    private addTooltipsExtension(extensions: Extension[]) {
        if (isAbsent(this.settings.tooltips)) {
            return extensions;
        }

        if (Object.keys(this.settings.tooltips!).length > 0) {
            return extensions.concat(tooltipExtension(this.settings.tooltips));
        }
        return extensions;
    }

    /**
     * Currently folding ranges are supporting only code that is placed between '{...}'
     * Braces would be kept, but the code will be hidden
     * @param editorView
     */
    private setupFoldingRanges(editorView: CodeEditorView) {
        requireNotNull(this.settings.foldRanges).map(range => {
            const braceOpenPos = editorView.getLineBy(range.from).text.indexOf("{");
            const braceClosePos = editorView.getLineBy(range.to).text.indexOf("}");

            requireNotNull(braceOpenPos);
            requireNotNull(braceClosePos);

            sanityCheck(braceOpenPos >= 0, "opening brace is not found for folding");
            sanityCheck(braceClosePos >= 0, "closing brace is not found for folding");

            const start = editorView.getLineBy(range.from).from + braceOpenPos + 1;
            const end = editorView.getLineBy(range.to).from + braceClosePos;

            sanityCheck(start < end, "start must be before end");

            editorView.foldCode(start, end);
        });
    }
}
